import type { AgentCategory } from '../../types/platform'

export const INTAKE_TO_COMMUNICATION: AgentCategory[] = [
  {
    id: 'intake',
    label: 'Intake Agents',
    stage: 'Intake',
    headline: 'Bring Everything Into One Process.',
    purpose: 'Receive information from multiple channels and create a structured record.',
    agents: [
      {
        name: 'Web Application Agent',
        purpose: 'Collects submissions through a configurable web form and turns each one into a structured record.',
        inputs: ['Form responses', 'Attached files'],
        outputs: ['Structured record', 'Submission confirmation'],
        useCases: ['Founder applications', 'Vendor registration'],
        workflows: ['invest', 'select', 'buy'],
      },
      {
        name: 'Email Intake Agent',
        purpose: 'Reads inbound emails and attachments sent to a process inbox and creates or updates records.',
        inputs: ['Forwarded emails', 'Attachments'],
        outputs: ['New or updated record', 'Extracted documents'],
        useCases: ['Deal inbox', 'Proposal submissions'],
        workflows: ['invest', 'buy', 'acquire'],
      },
      {
        name: 'File Upload Agent',
        purpose: 'Accepts uploaded documents and classifies them against the record they belong to.',
        inputs: ['Decks', 'Financials', 'Policies'],
        outputs: ['Classified documents', 'Extracted fields'],
        useCases: ['Pitch deck uploads', 'Security questionnaires'],
        workflows: ['invest', 'buy', 'assure'],
      },
      {
        name: 'Data Room Agent',
        purpose: 'Indexes a shared data room so its contents can be searched and referenced during diligence.',
        inputs: ['Data room folders', 'Document sets'],
        outputs: ['Document index', 'Coverage checklist'],
        useCases: ['Late-stage diligence', 'Acquisition review'],
        workflows: ['invest', 'acquire', 'assure'],
      },
      {
        name: 'API Intake Agent',
        purpose: 'Receives records from other systems through an API and maps them into the process.',
        inputs: ['API payloads', 'System exports'],
        outputs: ['Mapped records', 'Sync status'],
        useCases: ['CRM handoff', 'Portal integration'],
        workflows: ['invest', 'buy', 'select', 'monitor'],
      },
    ],
  },
  {
    id: 'qualification',
    label: 'Qualification Agents',
    stage: 'Qualify',
    headline: 'Determine What Deserves Attention.',
    purpose: 'Compare opportunities against rules, requirements and organizational criteria.',
    agents: [
      {
        name: 'Industry Classification Agent',
        purpose: 'Assigns sector, sub-sector and business model labels to each record.',
        inputs: ['Company description', 'Website', 'Deck'],
        outputs: ['Sector labels', 'Business model'],
        useCases: ['Pipeline segmentation', 'Program tracks'],
        workflows: ['invest', 'select', 'buy'],
      },
      {
        name: 'Taxonomy Agent',
        purpose: 'Maps records to your organization’s own categories and tags.',
        inputs: ['Record fields', 'Your taxonomy'],
        outputs: ['Custom tags', 'Category mapping'],
        useCases: ['Internal reporting', 'Portfolio themes'],
        workflows: ['invest', 'select', 'monitor'],
      },
      {
        name: 'Requirements Fit Agent',
        purpose: 'Checks a submission against stated requirements and shows which are met, unmet or unclear.',
        inputs: ['Submission', 'Requirement list'],
        outputs: ['Requirement matrix', 'Gaps'],
        useCases: ['Vendor requirements', 'RFP compliance'],
        workflows: ['buy', 'select', 'acquire'],
      },
      {
        name: 'Thesis Fit Agent',
        purpose: 'Assesses an opportunity against a fund’s investment thesis, criterion by criterion.',
        inputs: ['Deck', 'Company data', 'Fund thesis'],
        outputs: ['Fit by criterion', 'Supporting evidence'],
        useCases: ['Deal screening', 'CVC strategic fit'],
        workflows: ['invest', 'acquire'],
      },
      {
        name: 'Eligibility Agent',
        purpose: 'Applies eligibility rules such as geography, stage or legal status before review begins.',
        inputs: ['Applicant data', 'Eligibility rules'],
        outputs: ['Eligible / ineligible / needs review', 'Reason'],
        useCases: ['Grant programs', 'Accelerator intake'],
        workflows: ['select', 'buy'],
      },
      {
        name: 'Risk Screening Agent',
        purpose: 'Flags early risk indicators that should be reviewed before deeper diligence.',
        inputs: ['Record', 'Public information', 'Risk rules'],
        outputs: ['Risk flags', 'Evidence links'],
        useCases: ['Third-party risk', 'Counterparty screening'],
        workflows: ['buy', 'assure', 'acquire'],
      },
      {
        name: 'Scoring Agent',
        purpose: 'Applies your weighted scoring model and explains how each score was reached.',
        inputs: ['Assessments', 'Scoring weights'],
        outputs: ['Scores with rationale', 'Ranked list'],
        useCases: ['Application ranking', 'Pipeline prioritization'],
        workflows: ['invest', 'select', 'buy'],
      },
    ],
  },
  {
    id: 'enrichment',
    label: 'Enrichment Agents',
    stage: 'Enrich',
    headline: 'Turn Incomplete Information Into Intelligence.',
    purpose: 'Find missing information, enrich submissions and validate claims.',
    agents: [
      {
        name: 'Research Agent',
        purpose: 'Researches the organization, its market and its context from available sources.',
        inputs: ['Record', 'Research questions'],
        outputs: ['Research brief', 'Source list'],
        useCases: ['Company research', 'Vendor background'],
        workflows: ['invest', 'buy', 'acquire', 'assure'],
      },
      {
        name: 'Gap Analysis Agent',
        purpose: 'Identifies the information still needed to complete the assessment.',
        inputs: ['Record', 'Required fields', 'Criteria'],
        outputs: ['Missing information list', 'Suggested questions'],
        useCases: ['Pre-meeting prep', 'Incomplete applications'],
        workflows: ['invest', 'select', 'buy'],
      },
      {
        name: 'Company Enrichment Agent',
        purpose: 'Adds firmographic details such as team, location, funding and product information.',
        inputs: ['Company name', 'Website'],
        outputs: ['Enriched profile'],
        useCases: ['Inbound deals', 'Supplier records'],
        workflows: ['invest', 'buy', 'select'],
      },
      {
        name: 'Market Intelligence Agent',
        purpose: 'Assembles market size reasoning, trends and competitive context.',
        inputs: ['Sector', 'Company positioning'],
        outputs: ['Market brief', 'Assumptions'],
        useCases: ['Market sizing', 'Category review'],
        workflows: ['invest', 'acquire'],
      },
      {
        name: 'Verification Agent',
        purpose: 'Checks claims in a submission against supporting documents and other sources.',
        inputs: ['Claims', 'Documents', 'Sources'],
        outputs: ['Supported / unsupported claims', 'Evidence trail'],
        useCases: ['Traction claims', 'Certification claims'],
        workflows: ['invest', 'buy', 'assure'],
      },
      {
        name: 'Contradiction Agent',
        purpose: 'Finds inconsistencies between documents, answers and sources.',
        inputs: ['Document set', 'Questionnaire answers'],
        outputs: ['Contradictions', 'Follow-up questions'],
        useCases: ['Data room review', 'Questionnaire review'],
        workflows: ['invest', 'acquire', 'assure'],
      },
    ],
  },
  {
    id: 'communication',
    label: 'Communication Agents',
    stage: 'Communicate',
    headline: 'Keep the Process Moving.',
    purpose: 'Request missing information and keep external parties engaged throughout diligence.',
    agents: [
      {
        name: 'Information Request Agent',
        purpose: 'Drafts requests for missing information based on identified gaps, for your team to send.',
        inputs: ['Gap list', 'Contact'],
        outputs: ['Request draft', 'Tracked request'],
        useCases: ['Diligence requests', 'Missing documents'],
        workflows: ['invest', 'buy', 'select', 'acquire'],
      },
      {
        name: 'Follow Up Agent',
        purpose: 'Tracks open requests and prepares reminders when responses are outstanding.',
        inputs: ['Open requests', 'Due dates'],
        outputs: ['Reminder drafts', 'Request status'],
        useCases: ['Overdue documents', 'Pending answers'],
        workflows: ['invest', 'buy', 'select'],
      },
      {
        name: 'Founder Communication Agent',
        purpose: 'Prepares founder-facing updates, questions and status messages.',
        inputs: ['Deal stage', 'Open questions'],
        outputs: ['Founder message drafts'],
        useCases: ['Meeting follow-ups', 'Pass notes'],
        workflows: ['invest', 'select'],
      },
      {
        name: 'Vendor Communication Agent',
        purpose: 'Prepares vendor-facing requests and updates during procurement review.',
        inputs: ['Review stage', 'Requirements'],
        outputs: ['Vendor message drafts'],
        useCases: ['Security questionnaire follow-up', 'Pilot coordination'],
        workflows: ['buy', 'assure'],
      },
      {
        name: 'Applicant Communication Agent',
        purpose: 'Keeps applicants informed about status, requirements and decisions.',
        inputs: ['Application status', 'Program rules'],
        outputs: ['Applicant updates'],
        useCases: ['Grant programs', 'Competitions'],
        workflows: ['select'],
      },
      {
        name: 'Stakeholder Update Agent',
        purpose: 'Summarizes progress for internal stakeholders and committees.',
        inputs: ['Process status', 'Findings'],
        outputs: ['Status summary'],
        useCases: ['Weekly pipeline updates', 'Committee pre-reads'],
        workflows: ['invest', 'buy', 'acquire', 'monitor'],
      },
    ],
  },
]
